import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import SafeIcon from '../components/common/SafeIcon';
import { useToast } from '../components/ui/Toast';
import { checkoutService } from '../services/checkout';
import { MENTORS } from '../data/mentors';
import { useGame } from '../context/GameContext';
import * as FiIcons from 'react-icons/fi';
import { formatNumber } from '../utils/formatters';

const { FiCheck, FiX, FiLoader } = FiIcons;

export default function MarketplaceSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const { success, error, warning } = useToast();
  const { state, dispatch } = useGame();

  // The Stripe session ID is expected in the URL query parameters upon successful redirect.
  const sessionId = searchParams.get('session_id');
  // The character key is retrieved from localStorage, where it was stored before redirecting.
  const characterKey = localStorage.getItem('mindgarden-pending-purchase');

  useEffect(() => {
    if (sessionId && characterKey) {
      // Important: Remove the key from localStorage immediately to prevent re-granting on page refresh.
      localStorage.removeItem('mindgarden-pending-purchase');
      processPayment();
    } else {
      setStatus('error');
      let errorMsg = 'Could not verify your purchase.';
      if (!sessionId) {
        errorMsg += ' The payment session identifier is missing.';
      }
      if (!characterKey) {
        errorMsg += ' Could not identify the purchased item.';
      }
      setMessage(errorMsg);
      error(errorMsg);

      // If the user navigates here manually or the session ID is missing,
      // clear any pending purchase to prevent accidental processing.
      if (characterKey) {
        localStorage.removeItem('mindgarden-pending-purchase');
        warning('Cleared incomplete purchase attempt.');
      }
    }
  }, [sessionId, characterKey]);

  const processPayment = async () => {
    try {
      setStatus('processing');
      setMessage('Verifying your purchase...');
      const result = await checkoutService.handleSuccess(sessionId);

      if (result.success) {
        setStatus('success');
        setMessage('Purchase completed successfully!');
        
        const mentor = MENTORS[characterKey];
        if (state && mentor) {
          if (!state.ownedCharacters.includes(characterKey)) {
            dispatch({ type: 'REFRESH_DATA', payload: { mentor } });
            success(`${mentor.name} unlocked! +${formatNumber(mentor.spsGrant)} SPS added.`);
          } else {
            success(`${mentor.name} is already owned.`);
          }
        } else {
          warning('Purchase verified, but could not update game state automatically.');
        }
        
        setTimeout(() => navigate('/marketplace'), 3000);
      } else {
        throw new Error(result.message || 'Payment processing failed');
      }
    } catch (err) {
      console.error('Payment processing error:', err);
      setStatus('error');
      setMessage(err.message || 'Failed to process payment');
      error('Payment processing failed. Please try again or contact support.');
    }
  };

  const getStatusIcon = () => {
    if (status === 'success') return FiCheck;
    if (status === 'error') return FiX;
    return FiLoader;
  };

  const getStatusColor = () => {
    if (status === 'success') return 'text-sage';
    if (status === 'error') return 'text-crimson';
    return 'text-skyblue';
  };

  const StatusIcon = getStatusIcon();

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="p-8 text-center max-w-md">
          <div
            className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
              status === 'success' ? 'bg-sage/20' : status === 'error' ? 'bg-crimson/20' : 'bg-skyblue/20'
            }`}
          >
            <SafeIcon icon={StatusIcon} className={`w-8 h-8 ${getStatusColor()} ${status === 'processing' ? 'animate-spin' : ''}`} />
          </div>
          <h1 className="text-2xl font-cormorant font-bold text-charcoal mb-2">
            {status === 'processing' && 'Processing Payment'}
            {status === 'success' && 'Payment Successful!'}
            {status === 'error' && 'Payment Failed'}
          </h1>
          <p className="text-charcoal/70 font-dm-sans mb-6">{message}</p>
          {status === 'success' && (
            <div className="mb-6 p-4 bg-sage/10 rounded-xl">
              <p className="text-sm text-sage font-medium">
                Redirecting to marketplace in 3 seconds...
              </p>
            </div>
          )}
          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => navigate('/marketplace')} className="flex-1">
              Back to Marketplace
            </Button>
            {status === 'error' && (
              <Button variant="primary" onClick={processPayment} className="flex-1">
                Retry
              </Button>
            )}
          </div>
          {sessionId && (
            <div className="mt-4 p-2 bg-gray-50 rounded text-xs text-gray-500 font-mono">
              Session: {sessionId}
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  );
}